import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeReviewApi, buildPredicate } from './mappingReview/api';
import FilterBar from './mappingReview/FilterBar';
import BulkPreviewDrawer from './mappingReview/BulkPreviewDrawer';
import RulesManager from './mappingReview/RulesManager';
import MismatchGroups from './mappingReview/MismatchGroups';

const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  fields: [],
  ahriBrands: [],
  candidateManufacturers: [],
  reviewReasons: [],
  programs: [],
  ahriTypes: [],
  scoreBucket: '',
  alignmentBucket: '',
  partStrengthPreset: '',
  partNumberRegex: '',
  sourceValueRegex: '',
  needsReviewOnly: false,
};

const SCORE_BAND = (score) => {
  const value = Number(score || 0);
  if (value >= 65) return { label: 'High', cls: 'text-bg-success' };
  if (value >= 45) return { label: 'Medium', cls: 'text-bg-warning' };
  return { label: 'Low', cls: 'text-bg-danger' };
};

function ScoreBadge({ score }) {
  const band = SCORE_BAND(score);
  return (
    <span className={`badge rounded-pill ${band.cls}`} title={`Score ${score}/100`}>
      {Number(score || 0).toFixed(1)} · {band.label}
    </span>
  );
}

function SignalBars({ signals }) {
  const entries = Object.entries(signals || {});
  if (!entries.length) return null;
  return (
    <div className="d-flex flex-wrap gap-2 mt-2 small">
      {entries.map(([key, value]) => (
        <span key={key} className="text-muted">
          <strong className="text-dark">{key.replace(/_/g, ' ')}:</strong>{' '}
          {Number(value).toFixed(2)}
        </span>
      ))}
    </div>
  );
}

function CandidateCard({ entry, ahriId, onApprove, onReject, busyKey }) {
  const candidate = entry.candidate || {};
  const hasCandidate = Boolean(entry.candidate);
  const isApproving = busyKey === `${ahriId}:${entry.id}:approve`;
  const isRejecting = busyKey === `${ahriId}:${entry.id}:reject`;
  const isBusy = isApproving || isRejecting;
  return (
    <div className="border rounded p-3 mb-2 bg-white">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div>
          <div className="fw-semibold">
            <span className="badge text-bg-light border me-2">{entry.field}</span>
            <span
              className="badge text-bg-info-subtle border border-info-subtle text-info-emphasis me-2"
              title="Approving keeps this product as a match and adds it to the next available slot for this field (becomes shoppable)."
            >
              {entry.slot && Number(entry.slot) > 1 ? `Approves → alternative (slot ${entry.slot})` : 'Approves → adds slot'}
            </span>
            <span className="text-muted small">source:</span>{' '}
            <code>{entry.source_value || entry.normalized_value || '—'}</code>
            {entry.normalized_value && entry.normalized_value !== entry.source_value && (
              <span className="text-muted small ms-2">
                (normalized <code>{entry.normalized_value}</code>)
              </span>
            )}
          </div>
          {entry.pattern && (
            <div className="small text-muted">
              Pattern: <code>{entry.pattern}</code>
            </div>
          )}
        </div>
        <ScoreBadge score={entry.score} />
      </div>

      {!hasCandidate && (
        <div className="alert alert-warning py-1 px-2 mb-2 small">
          No matching product was found for this value. Reject to dismiss, or find and map the
          correct product manually from the AHRI row.
        </div>
      )}

      <div className="row g-2 align-items-start">
        {candidate.image_url && (
          <div className="col-12 col-md-2">
            <img
              src={candidate.image_url}
              alt={candidate.product_code || ''}
              className="img-fluid rounded border"
              style={{ maxHeight: 96, objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        <div className={candidate.image_url ? 'col-12 col-md-10' : 'col-12'}>
          <div className="fw-semibold">
            {candidate.product_code || '—'}
            {candidate.manufacturer_part_number && (
              <span className="text-muted ms-2 small">
                MFR PN: {candidate.manufacturer_part_number}
              </span>
            )}
          </div>
          <div className="small">{candidate.name || ''}</div>
          <div className="small text-muted">
            {candidate.manufacturer_name || 'Unknown brand'}
            {candidate.category && <> · {candidate.category}</>}
          </div>

          {entry.review_reasons && entry.review_reasons.length > 0 && (
            <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small">
              <strong>Flagged for review:</strong> {entry.review_reasons.join('; ')}
            </div>
          )}

          {entry.reject_reasons && entry.reject_reasons.length > 0 && (
            <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small">
              <strong>Soft rejects:</strong> {entry.reject_reasons.join('; ')}
            </div>
          )}

          {entry.score_reasons && entry.score_reasons.length > 0 && (
            <details className="mt-2">
              <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                Why this score?
              </summary>
              <ul className="small mb-0 mt-1">
                {entry.score_reasons.map((r, idx) => (
                  <li key={idx}>{r}</li>
                ))}
              </ul>
              <SignalBars signals={entry.score_signals} />
            </details>
          )}
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => onReject(ahriId, entry)}
          disabled={isBusy}
        >
          {isRejecting ? 'Rejecting…' : 'Reject'}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => onApprove(ahriId, entry)}
          disabled={isBusy || !hasCandidate}
          title={hasCandidate ? undefined : 'No candidate product to approve'}
        >
          {isApproving ? 'Approving…' : `Approve & Apply ${candidate.product_code || ''}`.trim()}
        </button>
      </div>
    </div>
  );
}

function RowGroup({ row, onApprove, onReject, busyKey, expanded, onToggle }) {
  const ahriId = row.ahri_id;
  const pending = Array.isArray(row.pending) ? row.pending : [];
  return (
    <div className="card mb-3 shadow-sm">
      <div
        className="card-header bg-light d-flex flex-wrap justify-content-between align-items-center gap-2"
        role="button"
        onClick={() => onToggle(ahriId)}
      >
        <div>
          <span className="fw-semibold me-2">AHRI {ahriId}</span>
          {row.program_name && (
            <span className="badge text-bg-secondary me-1">{row.program_name}</span>
          )}
          {row.ahri_type && (
            <span className="badge text-bg-info text-dark me-1">{row.ahri_type}</span>
          )}
          {row.indoor_brand && (
            <span className="small text-muted me-2">Indoor: {row.indoor_brand}</span>
          )}
          {row.outdoor_brand && (
            <span className="small text-muted me-2">Outdoor: {row.outdoor_brand}</span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="badge text-bg-warning">{pending.length} pending</span>
          {row.complete_match && <span className="badge text-bg-success">Complete</span>}
          {!row.complete_match && row.partial_match && (
            <span className="badge text-bg-info text-dark">Partial</span>
          )}
          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>
      {expanded && (
        <div className="card-body">
          {pending.map((entry) => (
            <CandidateCard
              key={entry.id}
              entry={entry}
              ahriId={ahriId}
              onApprove={onApprove}
              onReject={onReject}
              busyKey={busyKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConfirmModal({ title, body, confirmLabel, confirmClass, onCancel, onConfirm, busy }) {
  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onCancel}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onCancel} aria-label="Close" />
          </div>
          <div className="modal-body">{body}</div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn ${confirmClass || 'btn-primary'}`}
              onClick={onConfirm}
              disabled={busy}
            >
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Needs Review means a real candidate was found but flagged (currently:
// brand disagreement) -- candidate:null ("no product match at all") is a
// distinct state, shown only via the explicit toggle below, never mixed
// into the default view or its counts. `has_candidate` is the backend's
// authoritative marker (isinstance(candidate, dict)) -- prefer it over
// probing a specific candidate_* field with `is_empty`, which can
// misclassify a real candidate whose own code fields happen to be blank.
const NO_CANDIDATE_COND = { field: 'has_candidate', op: 'is_false' };
const HAS_CANDIDATE_COND = { field: 'has_candidate', op: 'is_true' };

// Every predicate that drives a real action (list, live preview count, bulk
// approve/reject, saved-rule creation) must go through this so none of them
// can silently diverge from what the operator sees in the current view mode.
function withCandidateFilter(predicate, noCandidateOnly) {
  const cond = noCandidateOnly ? NO_CANDIDATE_COND : HAS_CANDIDATE_COND;
  if (!predicate) return { all: [cond] };
  const existing = Array.isArray(predicate.all) ? predicate.all : [predicate];
  return { all: [...existing, cond] };
}

export default function MappingReviewPanel({ apiBaseUrl, getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    rows_with_pending: 0,
    pending_entries: 0,
    rows_no_candidate: 0,
    no_candidate_entries: 0,
    by_field: [],
  });
  const [noCandidateOnly, setNoCandidateOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedPredicate, setAppliedPredicate] = useState(null);
  const [matchCount, setMatchCount] = useState(null);
  const [facets, setFacets] = useState({});
  const [busyKey, setBusyKey] = useState('');
  const [expanded, setExpanded] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [drawerAction, setDrawerAction] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [showGroups, setShowGroups] = useState(true);
  const previewTimer = useRef(null);

  const api = useMemo(() => makeReviewApi(apiBaseUrl, getAuthHeaders), [apiBaseUrl, getAuthHeaders]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await api.summary();
      setSummary({
        rows_with_pending: data.rows_with_pending || 0,
        pending_entries: data.pending_entries || 0,
        rows_no_candidate: data.rows_no_candidate || 0,
        no_candidate_entries: data.no_candidate_entries || 0,
        by_field: Array.isArray(data.by_field) ? data.by_field : [],
      });
    } catch (_e) {
      // Best-effort; the list call surfaces the real error.
    }
  }, [api]);

  const loadFacets = useCallback(async () => {
    try {
      setFacets((await api.facets(!noCandidateOnly)) || {});
    } catch (_e) {
      // Best-effort; filters degrade to empty option lists.
    }
  }, [api, noCandidateOnly]);

  const load = useCallback(
    async (nextPage, predicate, noCandidateOverride) => {
      const pageToLoad = nextPage === undefined ? page : nextPage;
      const pred = predicate === undefined ? appliedPredicate : predicate;
      const effectiveNoCandidateOnly = noCandidateOverride === undefined ? noCandidateOnly : noCandidateOverride;
      const effectivePred = withCandidateFilter(pred, effectiveNoCandidateOnly);
      setLoading(true);
      setError('');
      try {
        const data = await api.search(effectivePred, PAGE_SIZE, pageToLoad * PAGE_SIZE);
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotalRows(Number(data.total_rows || 0));
        setTotalEntries(Number(data.total_entries || 0));
        setPage(pageToLoad);
      } catch (err) {
        setError(err.message || String(err));
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [api, appliedPredicate, page, noCandidateOnly]
  );

  useEffect(() => {
    load(0, null);
    loadSummary();
    loadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced live match count for the current (unapplied) filter so the
  // operator sees the blast radius before bulk-acting. Must include the
  // candidate-presence condition -- otherwise this preview (and the "N
  // matches" it shows before a bulk action) disagrees with what bulk-acting
  // would actually touch.
  useEffect(() => {
    const predicate = buildPredicate(filters);
    if (!predicate) {
      setMatchCount(null);
      return undefined;
    }
    const effectivePredicate = withCandidateFilter(predicate, noCandidateOnly);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const data = await api.preview(effectivePredicate);
        setMatchCount({ entries: data.matched_entries, rows: data.matched_rows });
      } catch (_e) {
        setMatchCount(null);
      }
    }, 500);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [filters, api, noCandidateOnly]);

  const currentPredicate = useMemo(
    () => withCandidateFilter(buildPredicate(filters), noCandidateOnly),
    [filters, noCandidateOnly]
  );

  const applyFilter = useCallback(() => {
    const predicate = buildPredicate(filters);
    setAppliedPredicate(predicate);
    load(0, predicate);
  }, [filters, load]);

  const resetFilter = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setAppliedPredicate(null);
    setMatchCount(null);
    load(0, null);
    loadSummary();
  }, [load, loadSummary]);

  // Switches between "Needs Review" (candidate found, e.g. brand mismatch)
  // and "No Product Match" (candidate: null) -- a view mode, not a regular
  // filter, so it composes with whatever filter is applied rather than
  // replacing it. Passes the new value explicitly to `load` rather than
  // relying on `noCandidateOnly` state (which wouldn't be updated yet in
  // this render's closure).
  const toggleNoCandidateOnly = useCallback(
    (next) => {
      setNoCandidateOnly(next);
      // Facet counts (fields/brands/programs/score buckets) must scope to
      // the same view mode, or they'll disagree with what's on screen.
      api.facets(!next).then(setFacets).catch(() => {});
      // "Needs review only" (needs_review is_true) can never match a
      // no-candidate entry (_build_no_candidate_review_entry always sets
      // needs_review: false) -- clear it so the two don't silently AND into
      // an empty, unexplained result.
      if (next && filters.needsReviewOnly) {
        const nextFilters = { ...filters, needsReviewOnly: false };
        setFilters(nextFilters);
        const predicate = buildPredicate(nextFilters);
        setAppliedPredicate(predicate);
        load(0, predicate, next);
        return;
      }
      load(0, appliedPredicate, next);
    },
    [load, appliedPredicate, filters, api]
  );

  // Focus the queue on a single mismatch group (AHRI brand → candidate mfr).
  // Mirrors the group's predicate into the filter bar so the bulk bar + drawer
  // stay consistent, then loads the drill-in.
  const focusGroup = useCallback(
    (group) => {
      const next = {
        ...EMPTY_FILTERS,
        alignmentBucket: 'mismatch',
        ahriBrands: group.ahri_brand ? [group.ahri_brand] : [],
        candidateManufacturers: group.candidate_manufacturer ? [group.candidate_manufacturer] : [],
      };
      setFilters(next);
      const predicate = buildPredicate(next);
      setAppliedPredicate(predicate);
      // A manufacturer mismatch is inherently candidate-bearing (there's no
      // brand to compare without a candidate) -- force out of "No Product
      // Match" mode so this doesn't silently AND into an empty result.
      setNoCandidateOnly(false);
      api.facets(true).then(setFacets).catch(() => {});
      load(0, predicate, false);
      return predicate;
    },
    [load, api]
  );

  const handleGroupReview = useCallback(
    (group) => {
      focusGroup(group);
      setShowGroups(false);
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [focusGroup]
  );

  const handleGroupApprove = useCallback(
    (group) => {
      focusGroup(group);
      setDrawerAction('approve');
    },
    [focusGroup]
  );

  const handleToggle = useCallback((ahriId) => {
    setExpanded((prev) => ({ ...prev, [ahriId]: !prev[ahriId] }));
  }, []);

  const removeEntryLocal = useCallback((ahriId, entryId) => {
    setItems((prev) =>
      prev
        .map((row) => {
          if (row.ahri_id !== ahriId) return row;
          const next = row.pending.filter((p) => p.id !== entryId);
          return { ...row, pending: next };
        })
        .filter((row) => row.pending.length > 0)
    );
  }, []);

  const callAction = useCallback(
    async (ahriId, entry, action) => {
      const key = `${ahriId}:${entry.id}:${action}`;
      setBusyKey(key);
      try {
        const url = api.buildUrl(
          `/api/v1/ahri/mapping-review/${encodeURIComponent(ahriId)}/${action}`
        );
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            ...(typeof getAuthHeaders === 'function' ? getAuthHeaders() : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entry_id: entry.id,
            product_code: action === 'approve' ? entry.candidate?.product_code : undefined,
          }),
        });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(text || `${action} failed (${resp.status})`);
        }
        removeEntryLocal(ahriId, entry.id);
        loadSummary();
        loadFacets();
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setBusyKey('');
      }
    },
    [api, getAuthHeaders, loadSummary, loadFacets, removeEntryLocal]
  );

  const handleApprove = useCallback(
    (ahriId, entry) => {
      setConfirm({
        kind: 'approve',
        ahriId,
        entry,
        title: 'Approve match?',
        body: (
          <div>
            <p className="mb-1">
              Apply <code>{entry.candidate?.product_code}</code> to AHRI{' '}
              <strong>{ahriId}</strong> / <code>{entry.field}</code>?
            </p>
            <p className="text-muted small mb-0">
              This adds the candidate to <code>product_mapping</code>, links it on the
              product side, refreshes the AHRI Solr doc, and removes the entry from this
              review queue.
            </p>
          </div>
        ),
        confirmLabel: 'Approve & Apply',
        confirmClass: 'btn-primary',
      });
    },
    []
  );

  const handleReject = useCallback(
    (ahriId, entry) => {
      setConfirm({
        kind: 'reject',
        ahriId,
        entry,
        title: 'Reject candidate?',
        body: (
          <div>
            <p className="mb-1">
              Remove <code>{entry.candidate?.product_code}</code> from the review queue
              for AHRI <strong>{ahriId}</strong> / <code>{entry.field}</code>?
            </p>
            <p className="text-muted small mb-0">
              The AHRI row stays as-is. The next mapping run can re-surface it if the
              same candidate scores above the partial threshold again.
            </p>
          </div>
        ),
        confirmLabel: 'Reject',
        confirmClass: 'btn-outline-danger',
      });
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    if (!confirm) return;
    const action = confirm.kind === 'approve' ? 'approve' : 'reject';
    const { ahriId, entry } = confirm;
    await callAction(ahriId, entry, action);
    setConfirm(null);
  }, [callAction, confirm]);

  const pages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const hasFilter = Boolean(appliedPredicate);

  return (
    <div className="container-fluid p-0">
      {/* System messages bar (per app convention: above the content) */}
      {(error || notice) && (
        <div className="mb-2">
          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center mb-2" role="alert">
              <span>{error}</span>
              <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Dismiss" />
            </div>
          )}
          {notice && (
            <div className="alert alert-success d-flex justify-content-between align-items-center mb-0" role="status">
              <span>{notice}</span>
              <button type="button" className="btn-close" onClick={() => setNotice('')} aria-label="Dismiss" />
            </div>
          )}
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className={`btn btn-sm ${!noCandidateOnly ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => toggleNoCandidateOnly(false)}
        >
          Needs Review
        </button>
        <button
          type="button"
          className={`btn btn-sm ${noCandidateOnly ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => toggleNoCandidateOnly(true)}
        >
          No Product Match
          <span className="badge text-bg-light ms-1">{summary.rows_no_candidate}</span>
        </button>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">AHRI rows awaiting review</div>
              <div className="fs-3 fw-semibold">{summary.rows_with_pending}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">Total pending matches</div>
              <div className="fs-3 fw-semibold">{summary.pending_entries}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">AHRI rows with no product match</div>
              <div className="fs-3 fw-semibold">{summary.rows_no_candidate}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="card h-100">
            <div className="card-body d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="text-muted small mb-1">By field (click to filter)</div>
                <div className="d-flex flex-wrap gap-2">
                  {summary.by_field.length === 0 && (
                    <span className="text-muted">Nothing pending right now.</span>
                  )}
                  {summary.by_field.map((row) => {
                    const active = (filters.fields || []).includes(row.field);
                    return (
                      <button
                        key={row.field}
                        type="button"
                        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => {
                          const next = {
                            ...filters,
                            fields: active ? [] : [row.field],
                          };
                          setFilters(next);
                          const predicate = buildPredicate(next);
                          setAppliedPredicate(predicate);
                          load(0, predicate);
                        }}
                      >
                        {row.field} · {row.count}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="d-flex flex-column gap-1 ms-2 flex-shrink-0">
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setShowGroups((v) => !v)}
                >
                  {showGroups ? 'Hide mismatch groups' : 'Mismatch groups'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setShowRules((v) => !v)}
                >
                  {showRules ? 'Hide rules' : 'Rules & aliases'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showGroups && (
        <MismatchGroups
          api={api}
          onReview={handleGroupReview}
          onApprove={handleGroupApprove}
          onError={(msg) =>
            String(msg).toLowerCase().includes('alias added') ? setNotice(msg) : setError(msg)
          }
          onChanged={() => {
            load(0);
            loadSummary();
            loadFacets();
          }}
        />
      )}

      {showRules && (
        <div className="mb-3">
          <RulesManager
            api={api}
            currentPredicate={currentPredicate}
            currentLabel={
              currentPredicate
                ? currentPredicate.all
                    .map((c) =>
                      c.field
                        ? `${c.field} ${c.op} ${c.value !== undefined ? JSON.stringify(c.value) : ''}`.trim()
                        : c.any
                        ? `(${c.any.map((a) => `${a.field} ${a.op} ${JSON.stringify(a.value)}`).join(' OR ')})`
                        : ''
                    )
                    .filter(Boolean)
                    .join(' AND ')
                : ''
            }
            onError={(msg) => (String(msg).toLowerCase().includes('applied') ? setNotice(msg) : setError(msg))}
            onChanged={() => {
              load(0);
              loadSummary();
              loadFacets();
            }}
          />
        </div>
      )}

      <FilterBar
        filters={filters}
        onChange={setFilters}
        onApply={applyFilter}
        onReset={resetFilter}
        loading={loading}
        facets={facets}
      />

      {/* Bulk action bar — appears when a filter is active. */}
      {hasFilter && (
        <div className="card mb-3 border-primary">
          <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
            <div>
              <span className="fw-semibold">
                {matchCount ? `${matchCount.entries} matches across ${matchCount.rows} AHRI rows` : `${totalEntries} matches`}
              </span>
              <span className="text-muted small ms-2">select all matching this filter</span>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setDrawerAction('reject')}
                disabled={loading || totalEntries === 0}
              >
                Reject all matching
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setDrawerAction('approve')}
                disabled={loading || totalEntries === 0}
              >
                Approve all matching
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="text-center text-muted py-5">Loading review queue…</div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="text-center text-muted py-5">
          No low-confidence matches need review right now.
        </div>
      )}

      {items.map((row) => (
        <RowGroup
          key={row.ahri_id}
          row={row}
          onApprove={handleApprove}
          onReject={handleReject}
          busyKey={busyKey}
          expanded={!!expanded[row.ahri_id]}
          onToggle={handleToggle}
        />
      ))}

      {totalRows > PAGE_SIZE && (
        <nav aria-label="Mapping review pagination" className="d-flex justify-content-center mt-3">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => load(page - 1)}
                disabled={loading || page === 0}
              >
                Previous
              </button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                Page {page + 1} / {pages}
              </span>
            </li>
            <li className={`page-item ${page + 1 >= pages ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => load(page + 1)}
                disabled={loading || page + 1 >= pages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirm}
          busy={Boolean(busyKey)}
        />
      )}

      {drawerAction && appliedPredicate && (
        <BulkPreviewDrawer
          api={api}
          action={drawerAction}
          predicate={withCandidateFilter(appliedPredicate, noCandidateOnly)}
          onClose={() => setDrawerAction(null)}
          onDone={(job) => {
            setNotice(
              `Bulk ${drawerAction} complete: ${job.approved || 0} approved, ${job.rejected || 0} rejected, ${(job.errors || []).length} errors.`
            );
            load(0);
            loadSummary();
            loadFacets();
          }}
        />
      )}
    </div>
  );
}
